import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  sendFromClass,
  TClassSendBody,
  TClassSendResult,
  TPrepCallVariant,
  TRegLinkVariant,
} from '../../../requests/opsClassSends';
import { TOpsClassDetailEx } from '../../../requests/opsRegistrationsExtra';
import { btnSecondary, Card, labelClass } from '../ui';
import { fieldClass } from './shared';

type Props = {
  id: string;
  existing: TOpsClassDetailEx;
};

const VARIANTS: { value: TPrepCallVariant; label: string }[] = [
  { value: 'ft', label: 'Full-time instructor' },
  { value: 'part-time', label: 'Part-time instructor' },
  { value: 'series', label: 'Series' },
];

function outcome(r: TClassSendResult): string {
  const where =
    r.status === 'sent'
      ? `sent to ${r.to}`
      : r.status === 'held'
      ? `queued for ${r.to} (held: ${r.holdReason ?? 'review'})`
      : r.status === 'suppressed'
      ? `held — ${r.holdReason ?? 'quiet account'}`
      : r.status === 'failed'
      ? `failed to ${r.to}`
      : `${r.status} → ${r.to}`;
  const bits = [where];
  if (r.statusSet?.prepCallStatus) {
    bits.push(`prep call → ${r.statusSet.prepCallStatus}`);
  }
  if (r.statusSet?.travelStatus) {
    bits.push(`travel → ${r.statusSet.travelStatus}`);
  }
  if (r.kind === 'availability' && r.instructor) {
    bits.unshift(`asked ${r.instructor}`);
  }
  if (r.caseNumber) bits.push(`case ${r.caseNumber} opened`);
  else if (r.caseId) bits.push('case opened');
  if (r.financeCcMissing) bits.push('finance not copied (TRAVEL_CC unset)');
  if (r.count && r.count > 1) bits.push(`${r.count} messages`);
  if (r.warning) bits.push(r.warning);
  return bits.join(' · ');
}

const LABELS: Record<TClassSendResult['kind'], string> = {
  'prep-call': 'Prep-call request',
  travel: 'Travel trigger',
  'instructor-request': 'Instructor request',
  availability: 'Availability check',
  'reg-link': 'Registration link',
};

/**
 * Send from this class — the prep-call request, the travel trigger and the
 * formal instructor request, each making the status change the SOP makes
 * by hand afterwards. Only on an existing class.
 */
const SendSection: React.FC<Props> = ({ id, existing }) => {
  const queryClient = useQueryClient();
  const [variant, setVariant] = useState<TPrepCallVariant>(
    existing.series ? 'series' : 'ft',
  );
  const [prepTo, setPrepTo] = useState('');
  const [travelTo, setTravelTo] = useState('');
  const [reqTo, setReqTo] = useState('');
  const [note, setNote] = useState('');
  const [askWho, setAskWho] = useState('');
  const [linkVariant, setLinkVariant] = useState<TRegLinkVariant>('poc');
  const [linkTo, setLinkTo] = useState('');

  const send = useMutation({
    mutationFn: (body: TClassSendBody) => sendFromClass(id, body),
    onSuccess: (r) => {
      showToast(`${LABELS[r.kind]}: ${outcome(r)}`, 'success');
      queryClient.invalidateQueries({ queryKey: ['opsClass', id] });
      queryClient.invalidateQueries({ queryKey: ['opsClassRelated', id] });
      if (r.kind === 'instructor-request' || r.kind === 'availability') {
        setNote('');
      }
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const cancelled = existing.status === 'Cancelled';
  const instructor = existing.instructorName;
  const busy = send.isPending;
  const prep = existing.delivery?.prepCallStatus ?? null;
  const travel = existing.delivery?.travelStatus ?? null;

  return (
    <Card title="Send from this class">
      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2 xl:grid-cols-4">
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Prep-call request</label>
          <select
            className={fieldClass}
            value={variant}
            onChange={(e) => setVariant(e.target.value as TPrepCallVariant)}
            disabled={busy || cancelled}
          >
            {VARIANTS.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
          <input
            className={fieldClass}
            placeholder="To the POC on the class, or type another address"
            value={prepTo}
            onChange={(e) => setPrepTo(e.target.value)}
            disabled={busy || cancelled}
          />
          <button
            type="button"
            className={btnSecondary}
            disabled={busy || cancelled}
            onClick={() =>
              send.mutate({
                kind: 'prep-call',
                variant,
                ...(prepTo.trim() ? { to: prepTo.trim() } : {}),
              })
            }
          >
            Send prep-call request
          </button>
          <div className="text-xs text-gray-bold">
            You are copied, a case opens for the reply, and prep call status
            becomes Requested{prep ? ` (now: ${prep})` : ''}.
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className={labelClass}>Travel trigger</label>
          <input
            className={fieldClass}
            placeholder={
              instructor
                ? `To ${instructor}'s address on file, or type another`
                : 'No instructor on the class — type an address'
            }
            value={travelTo}
            onChange={(e) => setTravelTo(e.target.value)}
            disabled={busy || cancelled}
          />
          <button
            type="button"
            className={btnSecondary}
            disabled={busy || cancelled || (!instructor && !travelTo.trim())}
            onClick={() =>
              send.mutate({
                kind: 'travel',
                ...(travelTo.trim() ? { to: travelTo.trim() } : {}),
              })
            }
          >
            Send travel trigger
          </button>
          <div className="text-xs text-gray-bold">
            "Travel Required – BOOK TRAVEL" to the trainer, finance copied;
            travel status becomes Required – Initiated
            {travel ? ` (now: ${travel})` : ''}.
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className={labelClass}>Trainer</label>
          <input
            className={fieldClass}
            placeholder={
              instructor
                ? `Who to ask — ${instructor} unless you name another`
                : 'Who to ask, by name (any trainer, on the class or not)'
            }
            value={askWho}
            onChange={(e) => setAskWho(e.target.value)}
            disabled={busy || cancelled}
          />
          <input
            className={fieldClass}
            placeholder="Their address on file, or type another"
            value={reqTo}
            onChange={(e) => setReqTo(e.target.value)}
            disabled={busy || cancelled}
          />
          <textarea
            className={fieldClass}
            rows={2}
            placeholder="Proposed dates or windows, anything they should know (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy || cancelled}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={btnSecondary}
              disabled={
                busy ||
                cancelled ||
                (!instructor && !askWho.trim() && !reqTo.trim())
              }
              onClick={() =>
                send.mutate({
                  kind: 'availability',
                  ...(askWho.trim() ? { instructor: askWho.trim() } : {}),
                  ...(reqTo.trim() ? { to: reqTo.trim() } : {}),
                  ...(note.trim() ? { note: note.trim() } : {}),
                })
              }
            >
              Ask availability
            </button>
            <button
              type="button"
              className={btnSecondary}
              disabled={busy || cancelled || (!instructor && !reqTo.trim())}
              onClick={() =>
                send.mutate({
                  kind: 'instructor-request',
                  ...(reqTo.trim() ? { to: reqTo.trim() } : {}),
                  ...(note.trim() ? { note: note.trim() } : {}),
                })
              }
            >
              Request instructor
            </button>
          </div>
          <div className="text-xs text-gray-bold">
            Ask availability is the informal "are you free?" to any trainer — a
            case opens at With Scheduling for the yes/no. Request instructor is
            the formal ask to the trainer on the class — a case at Instructor
            Request. Either way their reply lands on the case.
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className={labelClass}>Registration link</label>
          <select
            className={fieldClass}
            value={linkVariant}
            onChange={(e) => setLinkVariant(e.target.value as TRegLinkVariant)}
            disabled={busy || cancelled}
          >
            <option value="poc">To the POC, to share</option>
            <option value="student">Straight to learners</option>
          </select>
          <input
            className={fieldClass}
            placeholder={
              linkVariant === 'poc'
                ? 'To the POC on the class, or type another address'
                : 'Learner addresses, comma-separated — one message each'
            }
            value={linkTo}
            onChange={(e) => setLinkTo(e.target.value)}
            disabled={busy || cancelled}
          />
          <button
            type="button"
            className={btnSecondary}
            disabled={
              busy || cancelled || (linkVariant === 'student' && !linkTo.trim())
            }
            onClick={() => {
              const addrs = linkTo
                .split(/[,;\s]+/)
                .map((s) => s.trim())
                .filter(Boolean);
              send.mutate({
                kind: 'reg-link',
                variant: linkVariant,
                ...(linkVariant === 'student'
                  ? { recipients: addrs }
                  : addrs[0]
                  ? { to: addrs[0] }
                  : {}),
              });
            }}
          >
            Send registration link
          </button>
          <div className="text-xs text-gray-bold">
            "Private Class Reg Link – POC / – Student" with this class's invite
            link
            {existing.links?.privateClassLink
              ? ''
              : existing.localOrigin
              ? ' (generated for this portal-born class; it resolves at cutover)'
              : ' — none on the class yet, add it on the Links panel'}
            .
          </div>
        </div>
      </div>
      {cancelled && (
        <div className="border-t border-white-regular px-5 py-3 text-xs text-gray-bold">
          The class is cancelled — nothing can be sent from it.
        </div>
      )}
    </Card>
  );
};

export default SendSection;
